/**
 * Line chart of consumption (L/100km) — ports `renderConsumption()` from
 * the legacy `static/analysis.js`. Per-point colors reflect the
 * estimation `quality` flag: exact = success, estimated = warning,
 * insufficient = muted.
 */

import { Line } from "react-chartjs-2";

import { ensureChartDefaults } from "./chartDefaults";
import { usePalette } from "./usePalette";
import type { ConsumptionPoint } from "../../../api/types";

export interface ConsumptionChartProps {
  points: ConsumptionPoint[];
}

export default function ConsumptionChart({ points }: ConsumptionChartProps) {
  const palette = usePalette();
  ensureChartDefaults(palette);

  const colorFor = (quality: ConsumptionPoint["quality"]) => {
    if (quality === "exact") return palette.success;
    if (quality === "estimated") return palette.warning;
    return palette.muted;
  };

  const data = {
    labels: points.map((p) => p.datetime.slice(0, 10)),
    datasets: [
      {
        label: "L/100km",
        data: points.map((p) => p.liters_per_100km),
        borderColor: palette.success,
        backgroundColor: palette.success + "22",
        pointBackgroundColor: points.map((p) => colorFor(p.quality)),
        pointBorderColor: points.map((p) => colorFor(p.quality)),
        pointRadius: 4,
        tension: 0.2,
        fill: true,
        // Use a dashed segment when the next point is "insufficient".
        segment: {
          borderDash: (ctx: { p1: { parsed?: unknown }; p1DataIndex?: number }) => {
            const idx = ctx.p1DataIndex;
            if (idx === undefined) return undefined;
            return points[idx]?.quality === "insufficient" ? [4, 4] : undefined;
          },
        },
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        ticks: {
          callback: (value: string | number) => Number(value).toFixed(1),
        },
      },
    },
    plugins: { legend: { display: false } },
  } as const;

  return <Line data={data} options={options} />;
}
